/**
 * VNIS Portal Account Selection Component
 * Used in both Information tab and Technical workflow
 */
import Paper from '@/components/Paper'
import { FilterSelectAsync } from '@/components/ui/filters'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getCustomerById } from '@/modules/Customer/services'
import {
    getServiceInformation,
    getServiceProviderByServiceTemplate,
    updateServiceInformations,
} from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { useEffect, useMemo } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { Loader2 } from 'lucide-react'

// ============================================================================
// Types
// ============================================================================

export interface SelectProviderProps {
    service: Service
    serviceTemplate: string
    serviceID: number
    serviceUUID: string
}

interface FormValues {
    has_entity?: boolean
    customer?: {
        label: string
        value: string
        email?: string
        organizationName?: string
        firstName?: string
        lastName?: string
        customerId?: string
    } | null
    email: string
    organization_name: string
    first_name: string
    last_name: string
    customer_id: string
}

// ============================================================================
// Component
// ============================================================================

const SelectProvider: React.FC<SelectProviderProps> = ({
    service,
    serviceTemplate,
    serviceID,
    serviceUUID,
}) => {
    const { t } = useTranslation()

    const { data: customer } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.CUSTOMERS, service?.customer_id],
        queryFn: () => getCustomerById(service?.customer_id),
    })

    const {
        data: informations,
        renderState,
        refetch: refetchServiceInformation,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.SERVICES_INFORMATIONS, serviceID],
        queryFn: () => getServiceInformation(serviceID),
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: updateServiceInformations,
        },
        {
            successMessage: t('update-provider-and-zone-information-success'),
            reactQueryKeys: QUERY_KEYS.SERVICES,
            onSuccess: () => {
                refetchServiceInformation()
            },
        }
    )

    const {
        control,
        watch,
        setValue,
        reset,
        handleSubmit,
        formState: { isDirty },
    } = useForm<FormValues>({
        defaultValues: {
            has_entity: false,
            customer: null,
            email: '',
            organization_name: '',
            first_name: '',
            last_name: '',
            customer_id: '',
        },
    })

    const isServiceLinked = useMemo(() => {
        return !!serviceUUID
    }, [serviceUUID])

    const hasEntity = watch('has_entity')
    const customerId = watch('customer_id')

    useEffect(() => {
        if (informations?.results || customer?.result) {
            const emailInfo = informations?.results?.find(
                (info) => info.name === 'email'
            )?.value
            const orgNameInfo = informations?.results?.find(
                (info) => info.name === 'organization_name'
            )?.value
            const firstNameInfo = informations?.results?.find(
                (info) => info.name === 'first_name'
            )?.value
            const lastNameInfo = informations?.results?.find(
                (info) => info.name === 'last_name'
            )?.value
            const customerIdInfo = informations?.results?.find(
                (info) => info.name === 'customer_id'
            )?.value

            const defaultEmail =
                emailInfo || customer?.result?.customer_email || ''
            const defaultOrgName =
                orgNameInfo ||
                customer?.result?.company?.company_name ||
                customer?.result?.customer_name ||
                ''
            const defaultFirstName = firstNameInfo || ''
            const defaultLastName = lastNameInfo || ''
            const defaultCustomerId = customerIdInfo || ''

            reset({
                has_entity: false,
                customer: defaultCustomerId
                    ? {
                          label: defaultOrgName,
                          value: defaultCustomerId,
                          organizationName: defaultOrgName,
                          firstName: defaultFirstName,
                          lastName: defaultLastName,
                          customerId: defaultCustomerId,
                          email: defaultEmail,
                      }
                    : null,
                email: defaultEmail,
                organization_name: defaultOrgName,
                first_name: defaultFirstName,
                last_name: defaultLastName,
                customer_id: defaultCustomerId,
            })
        }
    }, [informations, customer, reset])

    const onSubmit = async (values: FormValues) => {
        await mutateAsync({
            serviceID,
            data: [
                { name: 'email', value: values.email },
                { name: 'organization_name', value: values.organization_name },
                { name: 'first_name', value: values.first_name },
                { name: 'last_name', value: values.last_name },
                { name: 'customer_id', value: values.customer_id },
            ].filter((item) => item.value),
            message: 'Update provider information',
        })
    }

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <Paper className="px-4 pb-4 pt-2 bg-white rounded-lg mt-2">
            <p className="mb-4 text-md font-medium text-primary">
                {t('Portal')}
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Has Entity Checkbox - hidden when customer_id exists */}
                {!customerId && (
                    <Controller
                        control={control}
                        name="has_entity"
                        render={({ field }) => (
                            <div className="flex items-center space-x-2">
                                <Checkbox
                                    id="has_entity"
                                    checked={field.value}
                                    onCheckedChange={field.onChange}
                                />
                                <Label
                                    htmlFor="has_entity"
                                    className="text-primary cursor-pointer"
                                >
                                    {t('has_entity_question')}
                                </Label>
                            </div>
                        )}
                    />
                )}

                {/* Provider Select - shown when has_entity is true */}
                {hasEntity && (
                    <div className="space-y-2">
                        <Label>{t('Portal')}</Label>
                        <FilterSelectAsync
                            control={control}
                            name="customer"
                            placeholder={t('search_provider', {
                                defaultValue: 'Tìm kiếm provider...',
                            })}
                            searchPlaceholder={t('search_by_email', {
                                defaultValue: 'Tìm theo email...',
                            })}
                            asyncOptions={async (query) => {
                                const providers =
                                    await getServiceProviderByServiceTemplate(
                                        serviceTemplate,
                                        query
                                    )
                                return (providers || []).map((provider) => ({
                                    label: [
                                        provider.organizationName?.toUpperCase(),
                                        (provider?.firstName
                                            ? provider.firstName +
                                              ' ' +
                                              provider.lastName
                                            : ''
                                        )?.toUpperCase(),
                                        provider.email,
                                        t(
                                            'providers.states.' +
                                                provider.organizationState
                                        ),
                                    ]
                                        .filter(Boolean)
                                        .join(' - '),
                                    value: provider.customerId,
                                    ...provider,
                                }))
                            }}
                            getDefaultOptions={async (currentValue) => {
                                if (!currentValue?.email) return []
                                const providers =
                                    await getServiceProviderByServiceTemplate(
                                        serviceTemplate,
                                        currentValue.email
                                    )
                                return (providers || []).map((provider) => ({
                                    label: [
                                        provider.organizationName?.toUpperCase(),
                                        (provider?.firstName
                                            ? provider.firstName +
                                              ' ' +
                                              provider.lastName
                                            : ''
                                        )?.toUpperCase(),
                                        provider.email,
                                        t(
                                            'providers.states.' +
                                                provider.organizationState
                                        ),
                                    ]
                                        .filter(Boolean)
                                        .join(' - '),
                                    value: provider.customerId,
                                    ...provider,
                                }))
                            }}
                            onChangeAfterHookForm={(option) => {
                                if (!option) {
                                    reset()
                                    return
                                }
                                setValue('email', option?.email || '')
                                setValue(
                                    'organization_name',
                                    option?.organizationName || ''
                                )
                                setValue('first_name', option?.firstName || '')
                                setValue('last_name', option?.lastName || '')
                                setValue(
                                    'customer_id',
                                    option?.customerId || ''
                                )
                            }}
                        />
                    </div>
                )}

                {/* Form Fields */}
                <div className="grid grid-cols-2 gap-4">
                    <Controller
                        control={control}
                        name="organization_name"
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="organization_name">
                                    {t('organization_name')}
                                </Label>
                                <Input
                                    {...field}
                                    id="organization_name"
                                    placeholder={t('organization_name')}
                                    disabled={isServiceLinked}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        control={control}
                        name="email"
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="email">{t('Email')}</Label>
                                <Input
                                    {...field}
                                    id="email"
                                    type="email"
                                    placeholder={t('Email')}
                                    disabled={isServiceLinked}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        control={control}
                        name="first_name"
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="first_name">
                                    {t('first_name')}
                                </Label>
                                <Input
                                    {...field}
                                    id="first_name"
                                    placeholder={t('first_name')}
                                    disabled={isServiceLinked}
                                />
                            </div>
                        )}
                    />

                    <Controller
                        control={control}
                        name="last_name"
                        render={({ field }) => (
                            <div className="space-y-2">
                                <Label htmlFor="last_name">
                                    {t('last_name')}
                                </Label>
                                <Input
                                    {...field}
                                    id="last_name"
                                    placeholder={t('last_name')}
                                    disabled={isServiceLinked}
                                />
                            </div>
                        )}
                    />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={isPending || !!serviceUUID || !isDirty}
                    >
                        {isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {t('save')}
                    </Button>
                </div>
            </form>
        </Paper>
    )
}

export default SelectProvider
